import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within ProjectProvider');
  return context;
};

export const PROJECT_INFO = {
  kazilink: {
    id: 'kazilink',
    name: 'KaziLink',
    domain: 'kazilink.co.ke',
    region: 'Kenya',
    currency: 'KES',
    currencySymbol: 'KSh',
    color: '#10b981',
    secondaryColor: '#f59e0b',
    icon: '🇰🇪',
    description: 'Kenya Services Marketplace',
    commissionRate: 5
  },
  sitedesk: {
    id: 'sitedesk',
    name: 'SiteDesk',
    domain: 'sitedeskapp.com',
    region: 'Perth, Western Australia',
    currency: 'AUD',
    currencySymbol: 'A$',
    color: '#0ea5e9',
    secondaryColor: '#6366f1',
    icon: '🇦🇺',
    description: 'Field Service Admin Platform',
    commissionRate: 0
  }
};

export const ProjectProvider = ({ children }) => {
  const { api, isAuthenticated } = useAuth();
  const [projects, setProjects] = useState([]);
  const [currentProjectId, setCurrentProjectId] = useState(() => {
    return localStorage.getItem('current_project') || null;
  });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});

  const fetchProjects = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      const data = await api('/api/projects/');
      setProjects(data);
      
      // Set default project if none selected
      if (!currentProjectId && data.length > 0) {
        setCurrentProjectId(data[0].project_id);
        localStorage.setItem('current_project', data[0].project_id);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated, currentProjectId]);

  const fetchStats = useCallback(async () => {
    if (!currentProjectId || !isAuthenticated) return;
    
    try {
      const data = await api(`/api/projects/${currentProjectId}/stats`);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [api, currentProjectId, isAuthenticated]);

  const switchProject = useCallback((projectId) => {
    setCurrentProjectId(projectId);
    localStorage.setItem('current_project', projectId);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchStats]);

  const currentProject = projects.find(p => p.project_id === currentProjectId);
  const projectType = currentProject?.type || 'kazilink';
  const projectInfo = PROJECT_INFO[projectType] || PROJECT_INFO.kazilink;

  return (
    <ProjectContext.Provider value={{
      projects,
      currentProject,
      currentProjectId,
      projectInfo,
      projectType,
      stats,
      loading,
      switchProject,
      refreshProjects: fetchProjects,
      refreshStats: fetchStats,
      isKazilink: projectType === 'kazilink',
      isSitedesk: projectType === 'sitedesk'
    }}>
      {children}
    </ProjectContext.Provider>
  );
};
